import React, { useState, useEffect } from 'react';
import { Check, X, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';

const QuizView = ({ data }) => {
    // 1. Normalize Input Data
    // New format: { title: "Title", questions: [...] }
    // Old format: { question: "...", options: [...] }
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    // State to track status of each question: { [index]: { selectedOption: 'a', isSubmitted: true } }
    const [userAnswers, setUserAnswers] = useState({});

    useEffect(() => {
        if (data) {
            if (data.questions && Array.isArray(data.questions)) {
                setQuestions(data.questions);
            } else if (data.options && data.question) {
                // Wrap single question in array
                setQuestions([data]);
            }
        }
    }, [data]);

    // Current Question
    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = userAnswers[currentQuestionIndex] || { selectedOption: null, isSubmitted: false };

    const handleSelect = (optionId) => {
        if (currentAnswer.isSubmitted) return;
        setUserAnswers(prev => ({
            ...prev,
            [currentQuestionIndex]: { selectedOption: optionId, isSubmitted: true }
        }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const [showResults, setShowResults] = useState(false);

    const finishQuiz = () => {
        let score = 0;
        const answersPayload = [];

        questions.forEach((q, idx) => {
            const ans = userAnswers[idx];
            let answerText = null;

            if (ans && ans.selectedOption) {
                const selectedOpt = q.options.find(o => o.id === ans.selectedOption);
                if (selectedOpt) {
                    // Documentation requires ID (e.g., 'a', 'b') if available, otherwise text
                    answerText = selectedOpt.id;
                    if (selectedOpt.isCorrect) {
                        score++;
                    }
                }
            }

            // Add to payload
            // Note: Use q.id if available, otherwise fallback might be needed but backend expects question_id
            if (q.id) {
                answersPayload.push({
                    question_id: q.id,
                    user_answer: answerText || "" // Send empty string if no answer (?)
                });
            }
        });

        setShowResults(true);
        if (typeof data.onComplete === 'function') {
            const percentage = Math.round((score / questions.length) * 100);
            data.onComplete({
                percentage,
                score,
                total: questions.length,
                passed: percentage >= 75,
                answers: answersPayload
            });
        }
    };

    const resetQuiz = () => {
        setUserAnswers({});
        setCurrentQuestionIndex(0);
        setShowResults(false);
    };

    if (showResults) {
        let score = 0;
        questions.forEach((q, idx) => {
            const ans = userAnswers[idx];
            if (ans && ans.selectedOption) {
                const selectedOpt = q.options.find(o => o.id === ans.selectedOption);
                if (selectedOpt && selectedOpt.isCorrect) score++;
            }
        });
        const percentage = Math.round((score / questions.length) * 100);

        return (
            <div className="flex flex-col items-center justify-center p-8 h-full">
                <div className="text-6xl mb-4">
                    {percentage >= 75 ? '🎉' : percentage >= 60 ? '😊' : '📚'}
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-2">Quiz Completato!</h3>
                <div className="text-5xl font-bold text-blue-600 mb-2">{score}/{questions.length}</div>
                <div className="text-xl text-gray-600 mb-6">{percentage}%</div>

                <p className="text-gray-600 mb-8 max-w-md text-center">
                    {percentage >= 75
                        ? 'Eccellente! Hai completato questo step del piano di studio.'
                        : 'Buon lavoro, ma puoi migliorare. Riprova per completare il lesson plan.'}
                </p>

                <button
                    onClick={resetQuiz}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                    Ricomincia
                </button>
            </div>
        );
    }

    if (questions.length === 0) return <div className="p-4 text-center text-gray-500">Dati quiz non validi.</div>;

    const { selectedOption, isSubmitted } = currentAnswer;

    return (
        <div className="h-full overflow-y-auto p-2 md:p-4">
            <div className="max-w-2xl mx-auto flex flex-col gap-4 md:gap-6 mt-2 md:mt-4">

                {/* Header with Title and Progress */}
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-xl font-bold text-gray-800">{data.title || "Quiz"}</h2>
                    <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        Domanda {currentQuestionIndex + 1} di {questions.length}
                    </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    ></div>
                </div>

                <div className="bg-white p-6 md:p-8 rounded-2xl md:rounded-3xl shadow-lg border border-slate-100">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Domanda {currentQuestionIndex + 1}
                    </span>
                    <h3 className="text-lg md:text-xl font-bold text-slate-800 mt-2 mb-6 md:mb-8 leading-relaxed">{currentQuestion.question}</h3>

                    <div className="space-y-3">
                        {currentQuestion.options.map((option) => {
                            let baseStyle = "w-full p-4 rounded-xl border-2 text-left transition-all duration-200 flex justify-between items-center group";
                            let stateStyle = "border-slate-100 hover:border-indigo-200 hover:bg-indigo-50 bg-white text-slate-700";

                            if (isSubmitted) {
                                if (option.isCorrect) {
                                    stateStyle = "border-green-500 bg-green-50 text-green-800 font-medium";
                                } else if (selectedOption === option.id) {
                                    stateStyle = "border-red-500 bg-red-50 text-red-800";
                                } else {
                                    stateStyle = "border-slate-100 opacity-40";
                                }
                            }

                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => handleSelect(option.id)}
                                    disabled={isSubmitted}
                                    className={`${baseStyle} ${stateStyle}`}
                                >
                                    <span className="flex items-center gap-3 w-full">
                                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border ${isSubmitted && option.isCorrect ? 'border-green-600 bg-green-600 text-white' : 'border-slate-300 text-slate-500 group-hover:border-indigo-400'}`}>
                                            {option.id.toUpperCase()}
                                        </span>
                                        <span className="flex-1">{option.text}</span>
                                    </span>
                                    {isSubmitted && option.isCorrect && <Check size={20} className="text-green-600 flex-shrink-0 ml-2" />}
                                    {isSubmitted && !option.isCorrect && selectedOption === option.id && <X size={20} className="text-red-600 flex-shrink-0 ml-2" />}
                                </button>
                            );
                        })}
                    </div>

                    {/* Explanation */}
                    {isSubmitted && (
                        <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className={`p-5 rounded-2xl ${selectedOption && currentQuestion.options.find(o => o.id === selectedOption)?.isCorrect ? 'bg-green-100/50 border border-green-200' : 'bg-red-50 border border-red-100'}`}>
                                <p className={`font-bold text-sm mb-2 ${selectedOption && currentQuestion.options.find(o => o.id === selectedOption)?.isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                                    {selectedOption && currentQuestion.options.find(o => o.id === selectedOption)?.isCorrect ? 'Risposta Corretta!' : 'Risposta Errata'}
                                </p>
                                <p className="text-sm text-slate-700 leading-relaxed">{currentQuestion.explanation}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col-reverse md:flex-row justify-between items-stretch md:items-center mt-4 gap-3 md:gap-0 pb-8">
                    <button
                        type="button"
                        onClick={handlePrev}
                        disabled={currentQuestionIndex === 0}
                        className={`w-full md:w-auto px-6 py-3 rounded-xl flex items-center justify-center gap-2 font-medium ${currentQuestionIndex === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed hidden md:flex' : 'bg-white shadow text-gray-700 hover:bg-gray-50'}`}
                    >
                        <ChevronLeft size={20} /> Precedente
                    </button>

                    {isSubmitted && (
                        currentQuestionIndex < questions.length - 1 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 font-bold flex items-center justify-center gap-2 transform active:scale-95 transition-all"
                            >
                                Prossima Domanda <ChevronRight size={20} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={finishQuiz}
                                className="w-full md:w-auto px-6 py-3 bg-green-600 text-white rounded-xl shadow-lg hover:bg-green-700 font-bold flex items-center justify-center gap-2 transform active:scale-95 transition-all"
                            >
                                <Check size={18} /> Vedi Risultati
                            </button>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuizView;
